import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik, FormikProps } from 'formik';

import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/components/CustomRequestProvider/CustomRequestProvider';
import SearchFieldAuto from '@app/components/Forms/SearchField/SearchFieldAuto';
import { bodyOverflowRemove, bodyOverflowTransform } from '@app/components/LocalCommon/Utils/bodyOverflowTransform';
import { initCardsCount } from '@app/components/Pages/PetMemorial/PetMemorial';
import PetMemorialCard from '@app/components/Pages/PetMemorial/PetMemorialCard';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import PetMemorialModal from '@app/components/UI/PetMemorialModal/PetMemorialModal';
import { MemorialsRes } from '@app/objects/PetMemorial';
import '@app/scss/components/petMemorialBlock.scss';

const PetMemorialBlock: React.FC = () => {
	const [modalVisible, setModalVisible] = React.useState(false);
	const [petName, setPetName] = React.useState('');
	const [ownerName, setOwnerName] = React.useState('');
	const [firstRender, setFirstRender] = React.useState<boolean>(false);
	const params = useParams<{ id?: string }>();
	const navigate = useNavigate();

	const context = useItemsProviderContext<MemorialsRes>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { loading, error, items },
		actions: { handleChange },
	} = context;

	React.useEffect(() => {
		window?.scrollTo(0, 0);
		bodyOverflowTransform(true);

		return function cleanOverflow() {
			bodyOverflowRemove();
		};
	}, []);

	React.useEffect(() => {
		if (!isNaN(Number(params?.id))) {
			setModalVisible(true);
		} else {
			navigate('/pet-memorial');
			setModalVisible(false);
		}
	}, [params?.id]);

	React.useEffect(() => {
		if (!firstRender) {
			setFirstRender(true);
		}

		if (firstRender) {
			handleChange({
				offset: 0,
				count: initCardsCount,
				owner: ownerName,
				pet: petName,
			});
		}
	}, [petName, ownerName]);

	return (
		<div className="pet-memorial-body__wrapper">
			<div className="pet-memorial-body">
				<div className="pet-memorial-body__nav">
					<Formik
						onSubmit={(values) => {
							setPetName(values.petName);
							setOwnerName(values.ownerName);
						}}
						initialValues={{
							petName: '',
							ownerName: '',
						}}
					>
						{(formikBag: FormikProps<any>) => {
							return (
								<Form autoComplete="off">
									<SearchFieldAuto
										name="petName"
										placeholder="Search memorials by pet name"
										formikBag={formikBag}
										onChange={(value, option) => {
											formikBag.setFieldValue('petName', value);
										}}
										searchButton
										buttonType="submit"
									/>
									<SearchFieldAuto
										name="ownerName"
										placeholder="Search memorials by owner name"
										formikBag={formikBag}
										onChange={(value, option) => {
											formikBag.setFieldValue('ownerName', value);
										}}
										searchButton
										buttonType="submit"
									/>
								</Form>
							);
						}}
					</Formik>
				</div>

				{!error.length ? (
					items.length ? (
						<div className="pet-memorial-body__content ">
							{!loading && items.map((memorial, index) => (
								<PetMemorialCard
									key={memorial.id}
									memorial={memorial}
									setModalVisible={setModalVisible}
								/>
							))}
						</div>
					) : (
						!loading ? <div className="format-error-message">Nothing found</div> : null
					)
				) : (
					<div className="format-error-message">{error}</div>
				)}

				{loading && <BounceLoader />}
				<LoadMoreButton
					className="btn btn-secondary btn-load-more"
					textButton="Load more memorials"
					visible={!loading}
				/>
			</div>

			{modalVisible && !isNaN(Number(params?.id)) && (
				<CustomRequestProvider>
					<ItemProvider
						type={`memorials/${params.id}`}
						loadRequest={`memorials/${params.id}`}
						id={Number(params.id)}
					>
						<PetMemorialModal
							modalVisible={modalVisible}
							setModalVisible={setModalVisible}
						/>
					</ItemProvider>
				</CustomRequestProvider>
			)}
		</div>
	);
};

export default PetMemorialBlock;
